// =============================================================================
// Module Project - Repository: Activity Log
// Seul fichier autorisé à importer supabase pour les opérations d'activité
// =============================================================================

import { supabase } from '@/integrations/supabase/client';
import type { TaskActivityLog, ActivityAction } from '../types';
import type { Json } from '@/integrations/supabase/types';

// ─────────────────────────────────────────────────────────────────────────────
// Helper to convert Json to Record
// ─────────────────────────────────────────────────────────────────────────────
function jsonToRecord(json: Json | null): Record<string, unknown> | null {
  if (json === null || typeof json !== 'object' || Array.isArray(json)) {
    return null;
  }
  return json as Record<string, unknown>;
}

// ─────────────────────────────────────────────────────────────────────────────
// FETCH ACTIVITY LOGS
// ─────────────────────────────────────────────────────────────────────────────
export async function fetchActivityLogs(taskId: string): Promise<TaskActivityLog[]> {
  const { data, error } = await supabase
    .from('project_task_activity_log')
    .select('*')
    .eq('task_id', taskId)
    .order('created_at', { ascending: false })
    .limit(50);

  if (error) {
    console.error('Error fetching activity logs:', error);
    return [];
  }

  const items = data || [];

  // Fetch user profiles separately (user_id references auth.users, not core_profiles)
  const userIds = [...new Set(items.map(i => i.user_id).filter(Boolean))] as string[];
  let profileMap: Record<string, { full_name: string | null; avatar_url: string | null }> = {};

  if (userIds.length > 0) {
    const { data: profiles } = await supabase
      .from('core_profiles')
      .select('id, full_name, avatar_url')
      .in('id', userIds);

    if (profiles) {
      for (const p of profiles) {
        profileMap[p.id] = { full_name: p.full_name, avatar_url: p.avatar_url };
      }
    }
  }

  return items.map((item) => ({
    id: item.id,
    org_id: item.org_id,
    task_id: item.task_id,
    user_id: item.user_id,
    action: item.action as ActivityAction,
    field_name: item.field_name,
    old_value: item.old_value,
    new_value: item.new_value,
    metadata: jsonToRecord(item.metadata),
    created_at: item.created_at,
    user: item.user_id && profileMap[item.user_id] ? profileMap[item.user_id] : undefined,
  }));
}

// ─────────────────────────────────────────────────────────────────────────────
// CREATE ACTIVITY LOG
// ─────────────────────────────────────────────────────────────────────────────
export async function createActivityLog(
  orgId: string,
  taskId: string,
  userId: string | null,
  action: ActivityAction,
  fieldName?: string,
  oldValue?: string | null,
  newValue?: string | null,
  metadata?: Record<string, unknown>
): Promise<void> {
  const insertData = {
    org_id: orgId,
    task_id: taskId,
    user_id: userId,
    action: action as string,
    field_name: fieldName || null,
    old_value: oldValue || null,
    new_value: newValue || null,
    metadata: (metadata || null) as Json,
  };

  const { error } = await supabase
    .from('project_task_activity_log')
    .insert(insertData);

  if (error) {
    console.error('Error creating activity log:', error);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS pour créer des logs typés
// ─────────────────────────────────────────────────────────────────────────────
export async function logStatusChange(
  orgId: string,
  taskId: string,
  userId: string,
  oldStatus: string,
  newStatus: string
): Promise<void> {
  await createActivityLog(orgId, taskId, userId, 'status_changed', 'status_code', oldStatus, newStatus);
}

export async function logPriorityChange(
  orgId: string,
  taskId: string,
  userId: string,
  oldPriority: string | null,
  newPriority: string | null
): Promise<void> {
  await createActivityLog(orgId, taskId, userId, 'priority_changed', 'priority', oldPriority, newPriority);
}

export async function logAssignment(
  orgId: string,
  taskId: string,
  userId: string,
  assigneeName: string
): Promise<void> {
  await createActivityLog(orgId, taskId, userId, 'assigned', 'assignee', null, assigneeName);
}

export async function logUnassignment(
  orgId: string,
  taskId: string,
  userId: string,
  assigneeName: string
): Promise<void> {
  await createActivityLog(orgId, taskId, userId, 'unassigned', 'assignee', assigneeName, null);
}

export async function logDueDateChange(
  orgId: string,
  taskId: string,
  userId: string,
  oldDate: string | null,
  newDate: string | null
): Promise<void> {
  await createActivityLog(orgId, taskId, userId, 'due_date_changed', 'due_at', oldDate, newDate);
}

export async function logSubtaskAdded(
  orgId: string,
  taskId: string,
  userId: string,
  subtaskTitle: string
): Promise<void> {
  await createActivityLog(orgId, taskId, userId, 'subtask_added', 'subtask', null, subtaskTitle);
}

export async function logSubtaskCompleted(
  orgId: string,
  taskId: string,
  userId: string,
  subtaskTitle: string
): Promise<void> {
  await createActivityLog(orgId, taskId, userId, 'subtask_completed', 'subtask', null, subtaskTitle);
}

export async function logOwnerChange(
  orgId: string,
  taskId: string,
  userId: string,
  oldOwnerName: string | null,
  newOwnerName: string | null
): Promise<void> {
  await createActivityLog(orgId, taskId, userId, 'owner_changed', 'owner', oldOwnerName, newOwnerName);
}

export async function logEstimationChange(
  orgId: string,
  taskId: string,
  userId: string,
  oldHours: number | null,
  newHours: number | null
): Promise<void> {
  await createActivityLog(
    orgId, 
    taskId, 
    userId, 
    'estimation_changed', 
    'estimated_hours', 
    oldHours?.toString() || null, 
    newHours?.toString() || null
  );
}

export async function logTimeEntry(
  orgId: string,
  taskId: string,
  userId: string,
  minutes: number
): Promise<void> {
  await createActivityLog(orgId, taskId, userId, 'time_logged', 'time_spent', null, `${minutes} min`, { minutes });
}

export async function logComplexityChange(
  orgId: string,
  taskId: string,
  userId: string,
  oldComplexity: string | null,
  newComplexity: string | null
): Promise<void> {
  await createActivityLog(orgId, taskId, userId, 'complexity_changed', 'complexity', oldComplexity, newComplexity);
}

export async function logTaskCreated(
  orgId: string,
  taskId: string,
  userId: string,
  taskTitle: string
): Promise<void> {
  await createActivityLog(orgId, taskId, userId, 'created', 'task', null, taskTitle);
}
